import type { ReactNode } from "react";
import { home } from "../content/home";
import { cn } from "../lib/cn";
import { AppLogo, type App } from "./AppLogo";
import { useLoopTime } from "./useLoopTime";

/**
 * "How it works" step 4: the real Intakall sidebar (dark, as in the product and the hero film),
 * with the cards popping in one at a time, newest on top, then starting again. Viewers who turn
 * motion off see all three cards, still. Decorative: the step text is the real content.
 */

// When each card appears (ms from the start of a loop), and when the loop starts again.
export const LIVE_CARDS_TIMELINE = { slack: 900, linear: 2100, pr: 3300, loop: 8000 } as const;

const m = home.hero.meeting;

const SB_BODY = "font-['DM_Sans',sans-serif]";
const SB_HEAD = "font-['Space_Grotesk',sans-serif]";
const SB_MONO = "font-['JetBrains_Mono',monospace]";

const CHIP_TONE: Record<string, string> = {
  progress: "text-[#FBBF24] bg-[rgba(251,191,36,0.10)] border-[rgba(251,191,36,0.22)]",
  review: "text-[#3B82F6] bg-[rgba(59,130,246,0.10)] border-[rgba(59,130,246,0.22)]",
  neutral: "text-[#94A3B8] bg-[rgba(148,163,184,0.10)] border-[rgba(148,163,184,0.22)]",
};

type Chip = { text: string; tone: string };
type LiveCard = { app: App; label: string; title: string; subtitle: string; chips: readonly Chip[] };

// Newest first, as the sidebar stacks them.
const CARDS: Array<LiveCard & { at: number }> = [
  { app: "github", label: "Pull Request", ...m.pr, at: LIVE_CARDS_TIMELINE.pr },
  { app: "linear", label: "Linear", ...m.linear, at: LIVE_CARDS_TIMELINE.linear },
  { app: "slack", label: "Slack", ...m.slack, chips: [], at: LIVE_CARDS_TIMELINE.slack },
];

export function LiveCardsScreen() {
  const t = useLoopTime(LIVE_CARDS_TIMELINE.loop);
  const shown = CARDS.filter((card) => t >= card.at);

  return (
    <div className={cn("flex h-full min-h-[390px] flex-col bg-[#1a1a2e] sm:min-h-0 text-[14px] leading-[1.55] text-[#e8eaed]", SB_BODY)}>
      <div className="flex justify-center px-4 py-3">
        <img src="/sidebar-logo.png" alt="" className="h-[18px] max-w-[60%]" />
      </div>
      <div className="flex-1 overflow-hidden px-3 pb-3 sm:px-4">
        {shown.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <div className="flex gap-[5px]">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="h-[7px] w-[7px] rounded-full bg-white opacity-40 motion-safe:animate-dot-bounce"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
            <p className="text-[13px] font-medium">{m.sidebar.listening}</p>
          </div>
        ) : (
          shown.map((card) => <Card key={card.label} card={card} />)
        )}
      </div>
    </div>
  );
}

function Card({ card }: { card: LiveCard }) {
  return (
    <div className="mb-1.5 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 motion-safe:animate-card-slide">
      <Header icon={<AppLogo app={card.app} />} label={card.label} />
      <p data-testid="live-card-title" className="pl-8 text-[13.5px] font-semibold leading-[1.4]">
        {card.title}
      </p>
      <p className={cn("pl-8 text-[11.5px] text-[#94A3B8]", SB_MONO, card.chips.length > 0 && "mb-1.5")}>
        {card.subtitle}
      </p>
      {card.chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-8">
          {card.chips.map((chip) => (
            <span
              key={chip.text}
              className={cn("rounded-md border px-2.5 py-[3px] text-[11px] font-medium", SB_MONO, CHIP_TONE[chip.tone])}
            >
              {chip.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="mb-1 flex items-center gap-2.5">
      <span className="w-[22px] text-center">{icon}</span>
      <span className={cn("flex-1 text-[12px] font-bold uppercase tracking-[0.06em] text-[#3B82F6]", SB_HEAD)}>
        {label}
      </span>
    </div>
  );
}
